import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Truck, Plus, Pencil, RefreshCw, X, AlertCircle, Phone, Mail } from 'lucide-react';
import API from '../api/api';
import {
  FmcsaStatusBadge, FmcsaResultPanel, FmcsaCheckButton, useFmcsaCheck, isFmcsaVerified,
} from './FmcsaCheck';

// The broker's directory of outside carriers. Adding one here (or from a rate
// confirmation) runs the FMCSA check: does the phone the carrier gave match the
// one FMCSA has on file for that USDOT / MC?

const EMPTY_FORM = {
  usdot_number: '', mc_number: '', phone: '',
  name: '', contact_name: '', email: '', notes: '', is_active: true,
};

const identityKey = (f) => [f.usdot_number, f.mc_number, f.phone].map((v) => (v || '').trim()).join('|');

const errorText = (err, fallback) => {
  const data = err.response?.data;
  if (!data || typeof data !== 'object') return fallback;
  if (data.detail) return data.detail;
  return Object.entries(data)
    .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`)
    .join(' | ') || fallback;
};

export function CarrierFormModal({ isOpen, carrier, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [checkedFor, setCheckedFor] = useState(null);   // identityKey the result belongs to
  const [acknowledged, setAcknowledged] = useState(false);
  const fmcsa = useFmcsaCheck();
  const { setResult, reset } = fmcsa;

  useEffect(() => {
    if (!isOpen) return;
    const initial = carrier
      ? {
          usdot_number: carrier.usdot_number || '', mc_number: carrier.mc_number || '',
          phone: carrier.phone || '', name: carrier.name || '',
          contact_name: carrier.contact_name || '', email: carrier.email || '',
          notes: carrier.notes || '', is_active: carrier.is_active,
        }
      : EMPTY_FORM;
    setForm(initial);
    setError(null);
    setAcknowledged(false);
    // An existing carrier's stored result stands until its DOT / MC / phone change.
    if (carrier?.fmcsa_result) {
      setResult(carrier.fmcsa_result);
      setCheckedFor(identityKey(initial));
    } else {
      reset();
      setCheckedFor(null);
    }
  }, [isOpen, carrier, setResult, reset]);

  const hasIdentity = Boolean(form.usdot_number.trim() || form.mc_number.trim());
  const resultIsCurrent = fmcsa.result && checkedFor === identityKey(form);
  const verified = resultIsCurrent && isFmcsaVerified(fmcsa.result.status);
  const needsAck = resultIsCurrent && !verified;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (['usdot_number', 'mc_number', 'phone'].includes(name)) setAcknowledged(false);
  };

  const runCheck = async () => {
    const result = await fmcsa.run(form);
    setCheckedFor(identityKey(form));
    setAcknowledged(false);
    // Fill the name from FMCSA when the operator hasn't typed one.
    if (result?.legal_name && !form.name.trim()) {
      setForm((p) => ({ ...p, name: result.legal_name }));
    }
    return result;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!carrier && !hasIdentity) {
      setError('Enter the carrier\'s USDOT or MC number so it can be checked against FMCSA.');
      return;
    }
    const payload = { ...form };
    // Check first if the identity hasn't been checked yet (or changed since).
    // A clean result saves straight through; anything else stops here so the
    // operator sees it and decides.
    if (hasIdentity && !resultIsCurrent) {
      const result = await runCheck();
      if (!isFmcsaVerified(result?.status)) return;
      if (!payload.name.trim() && result.legal_name) payload.name = result.legal_name;
    } else if (needsAck && !acknowledged) {
      setError('Confirm you\'ve verified this carrier another way, or fix the details above.');
      return;
    }
    if (!payload.name.trim()) {
      setError('Enter the carrier name.');
      return;
    }

    setSaving(true);
    try {
      const res = carrier
        ? await API.patch(`carriers/${carrier.id}/`, payload)
        : await API.post('carriers/', payload);
      onSaved(res.data);
      onClose();
    } catch (err) {
      setError(errorText(err, 'Failed to save carrier. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const inputClass = 'mt-1 block w-full border border-gray-300 rounded-md shadow-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500';
  const saveLabel = saving ? 'Saving…'
    : fmcsa.checking ? 'Checking FMCSA…'
    : hasIdentity && !resultIsCurrent ? 'Verify & save'
    : carrier ? 'Save changes' : 'Add carrier';

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-start sm:items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg my-8">
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{carrier ? 'Edit carrier' : 'Add carrier'}</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              The phone number is checked against what FMCSA has on file for this USDOT / MC.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && (
            <div className="flex items-start gap-2 rounded-md bg-red-50 p-3 text-sm text-red-800">
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              {error}
            </div>
          )}

          {/* Identity — what gets checked */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">USDOT</label>
              <input name="usdot_number" value={form.usdot_number} onChange={handleChange}
                     className={inputClass} placeholder="1585417" autoFocus={!carrier} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">MC</label>
              <input name="mc_number" value={form.mc_number} onChange={handleChange}
                     className={inputClass} placeholder="MC585417" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Phone</label>
              <input type="tel" name="phone" value={form.phone} onChange={handleChange}
                     className={inputClass} placeholder="(815) 630-5362" />
            </div>
          </div>

          <div className="flex items-center justify-between gap-3">
            <FmcsaCheckButton onClick={runCheck} checking={fmcsa.checking} disabled={!hasIdentity} />
            {fmcsa.result && !resultIsCurrent && (
              <span className="text-xs text-gray-500">Details changed — verify again.</span>
            )}
          </div>

          {resultIsCurrent && (
            <FmcsaResultPanel result={fmcsa.result} compact
                              onUseName={(name) => setForm((p) => ({ ...p, name }))} />
          )}

          {needsAck && (
            <label className="flex items-start gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)}
                     className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600" />
              Save anyway — I've confirmed this carrier another way (e.g. called the number FMCSA lists).
            </label>
          )}

          <div className="border-t border-gray-100 pt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700">Carrier name <span className="text-red-500">*</span></label>
              <input name="name" value={form.name} onChange={handleChange} className={inputClass}
                     placeholder="Filled in from FMCSA" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Contact</label>
              <input name="contact_name" value={form.contact_name} onChange={handleChange}
                     className={inputClass} placeholder="George" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Dispatch email</label>
              <input type="email" name="email" value={form.email} onChange={handleChange}
                     className={inputClass} placeholder="dispatch@carrier.com" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700">Notes</label>
              <textarea name="notes" rows="2" value={form.notes} onChange={handleChange} className={inputClass} />
            </div>
            {carrier && (
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" name="is_active" checked={form.is_active} onChange={handleChange}
                       className="h-4 w-4 rounded border-gray-300 text-primary-600" />
                Active
              </label>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={saving || fmcsa.checking || (needsAck && !acknowledged)}
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-50">
              {saveLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Carriers() {
  const [carriers, setCarriers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [rechecking, setRechecking] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState('active'); // active | attention | inactive | all

  const fetchCarriers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await API.get('carriers/', { params: { page_size: 200 } });
      setCarriers(res.data?.results ?? res.data);
    } catch {
      setError('Failed to load carriers.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCarriers(); }, [fetchCarriers]);

  const upsert = (saved) => setCarriers((prev) => {
    const rest = prev.filter((c) => c.id !== saved.id);
    return [...rest, saved].sort((a, b) => a.name.localeCompare(b.name));
  });

  const recheck = async (c) => {
    setRechecking(c.id);
    try {
      const res = await API.post(`carriers/${c.id}/fmcsa-check/`);
      upsert(res.data);
    } catch {
      setError(`Couldn't re-check ${c.name} against FMCSA.`);
    } finally {
      setRechecking(null);
    }
  };

  const filtered = useMemo(() => carriers.filter((c) => {
    const q = searchQuery.trim().toLowerCase();
    const matchesSearch = !q || [c.name, c.mc_number, c.usdot_number, c.contact_name, c.phone, c.email]
      .some((v) => (v || '').toLowerCase().includes(q));
    const matchesFilter = filter === 'all'
      || (filter === 'active' && c.is_active)
      || (filter === 'inactive' && !c.is_active)
      || (filter === 'attention' && c.is_active && !isFmcsaVerified(c.fmcsa_status));
    return matchesSearch && matchesFilter;
  }), [carriers, searchQuery, filter]);

  const attentionCount = carriers.filter((c) => c.is_active && !isFmcsaVerified(c.fmcsa_status)).length;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Carriers</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Outside carriers you tender loads to. Each one is checked against FMCSA when it's added.
          </p>
        </div>
        <button onClick={() => { setEditing(null); setModalOpen(true); }}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md shadow-sm">
          <Plus size={16} />
          Add carrier
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <input type="text" placeholder="Search by name, MC, USDOT, phone or email…"
               value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
               className="flex-1 min-w-[200px] border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
        <select value={filter} onChange={(e) => setFilter(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
          <option value="active">Active carriers</option>
          <option value="attention">Needs attention ({attentionCount})</option>
          <option value="inactive">Inactive</option>
          <option value="all">All carriers</option>
        </select>
      </div>

      {error && <div className="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-800">{error}</div>}

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <Truck size={40} className="mx-auto mb-3 opacity-40" />
          <p className="text-sm font-medium">
            {carriers.length === 0 ? 'No carriers yet — add your first one.' : 'No carriers match.'}
          </p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Carrier</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Contact</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">FMCSA</th>
                <th className="px-4 py-2.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((c) => (
                <tr key={c.id} className={`hover:bg-gray-50 ${c.is_active ? '' : 'opacity-60'}`}>
                  <td className="px-4 py-2.5">
                    <p className="text-sm font-medium text-gray-900">{c.name}</p>
                    <p className="text-xs text-gray-500">
                      {[c.usdot_number && `USDOT ${c.usdot_number}`, c.mc_number].filter(Boolean).join(' · ') || '—'}
                    </p>
                  </td>
                  <td className="px-4 py-2.5 hidden md:table-cell">
                    <div className="space-y-0.5 text-sm text-gray-600">
                      {c.contact_name && <div>{c.contact_name}</div>}
                      {c.phone && <div className="flex items-center gap-1.5"><Phone size={12} className="text-gray-400" />{c.phone}</div>}
                      {c.email && <div className="flex items-center gap-1.5"><Mail size={12} className="text-gray-400" />{c.email}</div>}
                      {!c.contact_name && !c.phone && !c.email && <span className="text-gray-400">—</span>}
                    </div>
                  </td>
                  <td className="px-4 py-2.5">
                    <FmcsaStatusBadge status={c.fmcsa_status} checkedAt={c.fmcsa_checked_at} />
                    {c.fmcsa_checked_at && (
                      <p className="text-[11px] text-gray-400 mt-0.5">
                        {new Date(c.fmcsa_checked_at).toLocaleDateString()}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    <button onClick={() => recheck(c)} disabled={rechecking === c.id}
                            title="Re-check against FMCSA"
                            className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded disabled:opacity-50">
                      <RefreshCw size={15} className={rechecking === c.id ? 'animate-spin' : ''} />
                    </button>
                    <button onClick={() => { setEditing(c); setModalOpen(true); }} title="Edit carrier"
                            className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded">
                      <Pencil size={15} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CarrierFormModal isOpen={modalOpen} carrier={editing}
                        onClose={() => setModalOpen(false)} onSaved={upsert} />
    </div>
  );
}

export default Carriers;
